import { makeAutoObservable } from 'mobx'
import { CallStore } from 'entities/Call'
import { ConversationsStore } from 'entities/Conversation'
import { ContactsStore } from 'entities/Contacts'
import { FeatureFlagsStore } from 'entities/FeatureFlags'
import { NumbersStore } from 'entities/Phone'
import { MessageStore } from 'entities/Message'
import { SubscriptionStore } from 'entities/Subscription'
import { OrganizationSettingsStore, UserSettingsStore } from 'entities/Settings'
import { BroadcastStore } from 'entities/Broadcast'
import { ComplianceStore } from 'entities/Compliance'
import { DataLogStore } from 'entities/DataLog/store/dataLogStore'
import { EventLogStore } from 'entities/EventLog'
import { InboxesStore } from 'entities/Inbox'
import { RemindersStore } from 'entities/Reminders'
import { MultiOrganizationStore, OrganizationStore } from 'entities/Organization'
import { UsersStore } from 'entities/Users'
import { CannesMessageStore } from 'entities/CannedMessages'
import { TenDlcLimitsStore } from 'entities/TenDlc'
import { BillingStore } from 'entities/Billing'
import { Websocket } from 'entities/WebSocket'
import { VCardStore } from 'entities/VCard'
import { UserPermissionsStore } from 'entities/UsersPermissions'
import { TextToSpeechStore } from 'entities/TextToSpeech'
import { TagsStore } from 'entities/Tags'
import { ShortLinkStore } from 'entities/ShortLink'
import { SegmentsStore } from 'entities/Segment'
import { MergeFieldStore } from 'entities/MergeField'
import { IntegrationsStore } from 'entities/Integrations'
import { AuthStore } from 'entities/Auth'
import { ConfigStore } from 'entities/Config'
import { AttachmentStore } from 'entities/Attachment'
import { AdminOrganizationPlanStore } from 'entities/Admin/customPrices/store'
import { ConversionStore } from 'entities/Conversion'
import { RcsStore } from 'entities/Rcs'
import { PermissionsStore } from 'entities/Permissions'
import { QuietHoursSendAnywayStore } from 'entities/QuietHours'
import { SmartViewsStore } from 'entities/SmartViews'

export type IEntitiesStore = {
  callStore: CallStore
  conversationStore: ConversationsStore
  contactsStore: ContactsStore
  featureFlagsStore: FeatureFlagsStore
  numbersStore: NumbersStore
  messageStore: MessageStore
  subscriptionStore: SubscriptionStore
  userSettingsStore: UserSettingsStore
  organizationSettingsStore: OrganizationSettingsStore
  broadcastStore: BroadcastStore
  complianceStore: ComplianceStore
  dataLogStore: DataLogStore<unknown>
  eventLogStore: EventLogStore
  inboxesStore: InboxesStore
  remindersStore: RemindersStore
  organizationStore: OrganizationStore
  usersStore: UsersStore
  cannesMessageStore: CannesMessageStore
  tenDlcLimitsStore: TenDlcLimitsStore
  billingStore: BillingStore
  websocket: Websocket
  vCardStore: VCardStore
  userPermissionsStore: UserPermissionsStore
  textToSpeechStore: TextToSpeechStore
  tagsStore: TagsStore
  shortLinkStore: ShortLinkStore
  segmentsStore: SegmentsStore
  multiOrganizationStore: MultiOrganizationStore
  mergeFieldStore: MergeFieldStore
  integrationsStore: IntegrationsStore
  authStore: AuthStore
  configStore: ConfigStore
  attachmentStore: AttachmentStore
  conversionStore: ConversionStore
  rcsStore: RcsStore
  permissionsStore: PermissionsStore
  adminOrganizationPlanStore: AdminOrganizationPlanStore
  quietHoursSendAnywayStore: QuietHoursSendAnywayStore
  smartViewsStore: SmartViewsStore
}

export class EntitiesStore implements IEntitiesStore {
  conversationStore: ConversationsStore
  contactsStore: ContactsStore
  featureFlagsStore: FeatureFlagsStore
  numbersStore: NumbersStore
  messageStore: MessageStore
  subscriptionStore: SubscriptionStore
  userSettingsStore: UserSettingsStore
  organizationSettingsStore: OrganizationSettingsStore
  broadcastStore: BroadcastStore
  complianceStore: ComplianceStore
  dataLogStore: DataLogStore<unknown>
  eventLogStore: EventLogStore
  inboxesStore: InboxesStore
  organizationStore: OrganizationStore
  usersStore: UsersStore
  cannesMessageStore: CannesMessageStore
  tenDlcLimitsStore: TenDlcLimitsStore
  callStore: CallStore
  billingStore: BillingStore
  remindersStore: RemindersStore
  websocket: Websocket
  vCardStore: VCardStore
  userPermissionsStore: UserPermissionsStore
  textToSpeechStore: TextToSpeechStore
  tagsStore: TagsStore
  shortLinkStore: ShortLinkStore
  segmentsStore: SegmentsStore
  multiOrganizationStore: MultiOrganizationStore
  mergeFieldStore: MergeFieldStore
  integrationsStore: IntegrationsStore
  authStore: AuthStore
  configStore: ConfigStore
  attachmentStore: AttachmentStore
  conversionStore: ConversionStore
  rcsStore: RcsStore
  permissionsStore: PermissionsStore
  adminOrganizationPlanStore: AdminOrganizationPlanStore
  quietHoursSendAnywayStore: QuietHoursSendAnywayStore
  smartViewsStore: SmartViewsStore

  constructor() {
    this.userSettingsStore = new UserSettingsStore()
    this.dataLogStore = new DataLogStore()
    this.organizationSettingsStore = new OrganizationSettingsStore()
    this.websocket = new Websocket()
    this.integrationsStore = new IntegrationsStore()
    this.billingStore = new BillingStore()
    this.vCardStore = new VCardStore()
    this.userPermissionsStore = new UserPermissionsStore()
    this.textToSpeechStore = new TextToSpeechStore()
    this.tagsStore = new TagsStore()
    this.shortLinkStore = new ShortLinkStore()
    this.segmentsStore = new SegmentsStore()
    this.multiOrganizationStore = new MultiOrganizationStore()
    this.configStore = new ConfigStore()
    this.attachmentStore = new AttachmentStore()
    this.conversionStore = new ConversionStore()
    this.adminOrganizationPlanStore = new AdminOrganizationPlanStore()

    this.messageStore = new MessageStore(this)
    this.featureFlagsStore = new FeatureFlagsStore(this.userSettingsStore)
    this.broadcastStore = new BroadcastStore(this)
    this.complianceStore = new ComplianceStore(this)
    this.eventLogStore = new EventLogStore(this)
    this.organizationStore = new OrganizationStore(this)
    this.tenDlcLimitsStore = new TenDlcLimitsStore(this)
    this.conversationStore = new ConversationsStore(this)
    this.subscriptionStore = new SubscriptionStore(this)
    this.usersStore = new UsersStore(this)
    this.numbersStore = new NumbersStore(this)
    this.cannesMessageStore = new CannesMessageStore(this.usersStore)
    this.mergeFieldStore = new MergeFieldStore(this.integrationsStore)
    this.contactsStore = new ContactsStore(
      this.conversationStore,
      this.subscriptionStore,
      this.usersStore,
      this.integrationsStore
    )
    this.smartViewsStore = new SmartViewsStore(this)
    this.inboxesStore = new InboxesStore(this)

    this.callStore = new CallStore(
      this.usersStore,
      this.inboxesStore,
      this.conversationStore,
      this.contactsStore,
      this.websocket,
      this.organizationStore
    )
    this.authStore = new AuthStore(
      this.usersStore,
      this.websocket,
      this.userSettingsStore,
      this.organizationStore
    )
    this.remindersStore = new RemindersStore(
      this.usersStore,
      this.subscriptionStore,
      this.organizationStore,
      this.billingStore,
      this.featureFlagsStore,
      this.authStore
    )
    this.rcsStore = new RcsStore(this)
    this.permissionsStore = new PermissionsStore()
    this.quietHoursSendAnywayStore = new QuietHoursSendAnywayStore()
    this.userSettingsStore.setEntities(this)
    this.conversationStore.setEntities(this)

    makeAutoObservable(this)
  }
}
